// Tank peint pièce par pièce (cosmétiques refaits) : couleur libre, matière, motif et autocollant par pièce.
// Chaque pièce est une base pleine, puis une couche « matière + motif + autocollant » découpée à la silhouette de la
// pièce par un masque. En partie, l'ensemble est cuit une fois en image (voir bakeTank) : le coût par image reste celui
// d'un sprite, quelle que soit la richesse du dessin. La hitbox reste le cercle serveur ; la couleur du camp reste au
// cœur de la tourelle, et les contours restent sombres pour que la silhouette se lise toujours.
import { Container, FillGradient, Graphics } from 'pixi.js';
import type { ClassNode } from '../../../../shared/catalog/classes';
import type { PartId, PartPaint, TankLook } from '../../../../shared/cosmetics';
import { ART_R, drawBarrel, drawHullFamily, type Palette } from './art';
import { drawFamilyDetail, drawRankPips } from './variants';
import { glossCore, shadeBarrels, shadeDome, shadeHull } from './shade';

const R = ART_R;

// ─── Couleurs ─────────────────────────────────────────────────────────

const ch = (c: number) => [(c >> 16) & 255, (c >> 8) & 255, c & 255];
const rgb = (r: number, g: number, b: number) => (Math.round(Math.max(0, Math.min(255, r))) << 16) | (Math.round(Math.max(0, Math.min(255, g))) << 8) | Math.round(Math.max(0, Math.min(255, b)));
export function mix(a: number, b: number, t: number): number {
  const [r1, g1, b1] = ch(a);
  const [r2, g2, b2] = ch(b);
  return rgb(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
const lighten = (c: number, t: number) => mix(c, 0xffffff, t);
const darken = (c: number, t: number) => mix(c, 0x000000, t);
function hueShift(c: number, deg: number): number {
  const [r, g, b] = ch(c).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h = (h * 60 + deg + 360) % 360;
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - C / 2;
  const [r1, g1, b1] = h < 60 ? [C, X, 0] : h < 120 ? [X, C, 0] : h < 180 ? [0, C, X] : h < 240 ? [0, X, C] : h < 300 ? [X, 0, C] : [C, 0, X];
  return rgb((r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255);
}
/** Contour d'une pièce : toujours sombre, teinté par sa couleur (silhouette lisible sur tout fond). */
const outlineOf = (c: number) => mix(darken(c, 0.78), 0x101317, 0.5);

/** Hachage déterministe (motifs et craquelures identiques chez tous les joueurs et à chaque cuisson). */
function seeded(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return (s >>> 0) / 4294967296;
  };
}

// ─── Silhouettes des pièces (repère du tank, rayon de référence ART_R) ─────

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function treadsShape(g: Graphics): Box {
  for (const side of [-1, 1]) g.roundRect(-0.68 * R, side * 0.66 * R - 0.19 * R, 1.36 * R, 0.38 * R, 0.12 * R);
  return { x: -0.68 * R, y: -0.85 * R, w: 1.36 * R, h: 1.7 * R };
}
function hullShape(g: Graphics): Box {
  g.roundRect(-0.64 * R, -0.52 * R, 1.28 * R, 1.04 * R, 0.18 * R);
  return { x: -0.64 * R, y: -0.52 * R, w: 1.28 * R, h: 1.04 * R };
}
function domeShape(g: Graphics): Box {
  g.circle(0, 0, 0.5 * R);
  return { x: -0.5 * R, y: -0.5 * R, w: R, h: R };
}

// ─── Matières ─────────────────────────────────────────────────────────

/** Remplissage de base d'une pièce selon sa matière (dégradé en coordonnées locales de la forme). */
function baseFill(p: PartPaint): number | FillGradient {
  const c = p.color;
  const lin = (stops: [number, number][], sx = 0, sy = 0, ex = 1, ey = 1) =>
    new FillGradient({ type: 'linear', start: { x: sx, y: sy }, end: { x: ex, y: ey }, colorStops: stops.map(([offset, color]) => ({ offset, color })), textureSpace: 'local' });
  switch (p.mat) {
    case 'mat:satin':
      return lin([[0, lighten(c, 0.22)], [0.55, c], [1, darken(c, 0.28)]]);
    case 'mat:brosse':
      return lin([[0, darken(c, 0.12)], [0.35, lighten(c, 0.2)], [0.65, c], [1, darken(c, 0.22)]], 0, 0, 0, 1);
    case 'mat:carbone':
      return darken(c, 0.62);
    case 'mat:chrome': {
      const k = mix(c, 0xb8c2cc, 0.5);
      return lin([[0, darken(k, 0.6)], [0.22, lighten(k, 0.5)], [0.42, darken(k, 0.35)], [0.58, lighten(k, 0.65)], [0.8, darken(k, 0.5)], [1, lighten(k, 0.15)]], 0, 0, 0.35, 1);
    }
    case 'mat:nacre':
      return lin([[0, lighten(hueShift(c, -35), 0.55)], [0.35, lighten(c, 0.45)], [0.7, lighten(hueShift(c, 45), 0.5)], [1, lighten(hueShift(c, 90), 0.35)]]);
    case 'mat:or': {
      const k = (v: number) => mix(v, c, 0.18);
      return lin([[0, k(0x8a6514)], [0.25, k(0xffe08a)], [0.5, k(0xb8860b)], [0.72, k(0xfff1bf)], [1, k(0x7a5a12)]], 0, 0, 0.5, 1);
    }
    case 'mat:neon':
      return darken(c, 0.82);
    case 'mat:magma':
      return mix(0x1d1512, c, 0.12);
    default:
      return c;
  }
}

/** Détails de matière par-dessus la base (découpés par le masque de la pièce). */
function materialOverlay(g: Graphics, p: PartPaint, b: Box, seed: number): void {
  const c = p.color;
  const rnd = seeded(seed);
  switch (p.mat) {
    case 'mat:brosse':
      for (let y = b.y; y < b.y + b.h; y += 1.1) g.moveTo(b.x, y).lineTo(b.x + b.w, y).stroke({ width: 0.45, color: rnd() < 0.5 ? 0xffffff : 0x000000, alpha: 0.08 + rnd() * 0.1 });
      break;
    case 'mat:carbone': {
      const s = 2.4;
      for (let x = b.x; x < b.x + b.w; x += s) {
        for (let y = b.y; y < b.y + b.h; y += s) {
          const odd = (Math.round((x - b.x) / s) + Math.round((y - b.y) / s)) % 2 === 0;
          g.rect(x, y, s, s).fill({ color: odd ? lighten(darken(c, 0.55), 0.18) : darken(c, 0.75), alpha: 0.9 });
          g.moveTo(x, y + (odd ? 0 : s)).lineTo(x + s, y + (odd ? s : 0)).stroke({ width: 0.4, color: 0xffffff, alpha: 0.12 });
        }
      }
      break;
    }
    case 'mat:chrome':
    case 'mat:or':
      // Reflet spéculaire en diagonale.
      g.poly([b.x + b.w * 0.15, b.y, b.x + b.w * 0.32, b.y, b.x + b.w * 0.12, b.y + b.h, b.x - b.w * 0.05, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.22 });
      g.poly([b.x + b.w * 0.4, b.y, b.x + b.w * 0.46, b.y, b.x + b.w * 0.26, b.y + b.h, b.x + b.w * 0.2, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.14 });
      break;
    case 'mat:nacre':
      for (let i = 0; i < 5; i++) {
        const y = b.y + (b.h * (i + 0.5)) / 5;
        g.moveTo(b.x, y).bezierCurveTo(b.x + b.w * 0.3, y - 3, b.x + b.w * 0.6, y + 3, b.x + b.w, y).stroke({ width: 1.6, color: lighten(hueShift(c, i * 50), 0.7), alpha: 0.25 });
      }
      break;
    case 'mat:magma': {
      // Craquelures incandescentes : lignes brisées lumineuses sur roche sombre.
      const hot = lighten(mix(c, 0xff6a1a, 0.6), 0.25);
      for (let k = 0; k < 7; k++) {
        let x = b.x + rnd() * b.w;
        let y = b.y + rnd() * b.h;
        g.moveTo(x, y);
        for (let s = 0; s < 4; s++) {
          x += (rnd() - 0.5) * b.w * 0.35;
          y += (rnd() - 0.5) * b.h * 0.35;
          g.lineTo(x, y);
        }
        g.stroke({ width: 2.4, color: hot, alpha: 0.35 }).stroke({ width: 0.9, color: lighten(hot, 0.6), alpha: 0.95 });
      }
      break;
    }
    default:
      break;
  }
}

// ─── Motifs ───────────────────────────────────────────────────────────

function patternOverlay(g: Graphics, p: PartPaint, b: Box, seed: number): void {
  if (!p.pat) return;
  const col = p.patColor;
  const rnd = seeded(seed * 7 + 3);
  const a = 0.85;
  switch (p.pat) {
    case 'pat:rayures':
      for (let x = b.x - b.h; x < b.x + b.w; x += 5.5) g.poly([x, b.y + b.h, x + 2.6, b.y + b.h, x + 2.6 + b.h, b.y, x + b.h, b.y]).fill({ color: col, alpha: a });
      break;
    case 'pat:damier': {
      const s = 4;
      for (let x = b.x; x < b.x + b.w; x += s) for (let y = b.y; y < b.y + b.h; y += s) if ((Math.round((x - b.x) / s) + Math.round((y - b.y) / s)) % 2 === 0) g.rect(x, y, s, s).fill({ color: col, alpha: a });
      break;
    }
    case 'pat:camo':
      for (let i = 0; i < Math.ceil((b.w * b.h) / 70); i++) {
        const x = b.x + rnd() * b.w;
        const y = b.y + rnd() * b.h;
        const r = 2.5 + rnd() * 4;
        const pts: number[] = [];
        for (let k = 0; k < 7; k++) {
          const t = (k / 7) * Math.PI * 2;
          const rr = r * (0.6 + rnd() * 0.7);
          pts.push(x + Math.cos(t) * rr * 1.4, y + Math.sin(t) * rr);
        }
        g.poly(pts).fill({ color: i % 2 ? col : mix(col, 0x000000, 0.35), alpha: 0.9 });
      }
      break;
    case 'pat:hexa': {
      const s = 3.2;
      for (let row = 0, y = b.y; y < b.y + b.h + s; row++, y += s * 1.5) {
        for (let x = b.x + (row % 2 ? s * 0.87 : 0); x < b.x + b.w + s; x += s * 1.73) g.regularPoly(x, y, s * 0.92, 6).stroke({ width: 0.8, color: col, alpha: a });
      }
      break;
    }
    case 'pat:ecailles': {
      const s = 3.6;
      for (let row = 0, y = b.y; y < b.y + b.h + s; row++, y += s * 0.75) {
        for (let x = b.x + (row % 2 ? s / 2 : 0); x < b.x + b.w + s; x += s) g.arc(x, y, s / 2, 0, Math.PI).stroke({ width: 0.9, color: col, alpha: a });
      }
      break;
    }
    case 'pat:circuit':
      for (let i = 0; i < Math.ceil((b.w * b.h) / 55); i++) {
        let x = b.x + Math.round((rnd() * b.w) / 2.5) * 2.5;
        let y = b.y + Math.round((rnd() * b.h) / 2.5) * 2.5;
        g.moveTo(x, y);
        for (let s = 0; s < 3; s++) {
          if (rnd() < 0.5) x += (rnd() < 0.5 ? -1 : 1) * 5;
          else y += (rnd() < 0.5 ? -1 : 1) * 5;
          g.lineTo(x, y);
        }
        g.stroke({ width: 0.8, color: col, alpha: a });
        g.circle(x, y, 0.9).fill({ color: col, alpha: 1 });
      }
      break;
    case 'pat:tigre':
      for (let x = b.x + 2; x < b.x + b.w; x += 4.5 + rnd() * 2) {
        const w = 1.2 + rnd() * 1.4;
        const top = rnd() < 0.5;
        const len = b.h * (0.35 + rnd() * 0.3);
        const y0 = top ? b.y : b.y + b.h;
        const dir = top ? 1 : -1;
        g.poly([x - w, y0, x + w, y0, x + w * 0.2 + dir * 0, y0 + dir * len, x - w * 0.3, y0 + dir * len * 0.9]).fill({ color: col, alpha: a });
      }
      break;
    case 'pat:flammes': {
      // Flammes peintes : langues depuis l'arrière (−x), vers l'avant.
      for (let i = 0; i < 5; i++) {
        const y = b.y + (b.h * (i + 0.5)) / 5;
        const len = b.w * (0.45 + rnd() * 0.35);
        const w = b.h / 5;
        g.moveTo(b.x - 2, y - w * 0.8)
          .bezierCurveTo(b.x + len * 0.4, y - w * 0.9, b.x + len * 0.7, y - w * 0.2, b.x + len, y)
          .bezierCurveTo(b.x + len * 0.7, y + w * 0.3, b.x + len * 0.4, y + w * 0.9, b.x - 2, y + w * 0.8)
          .fill({ color: col, alpha: 0.9 });
        g.moveTo(b.x - 2, y - w * 0.35)
          .bezierCurveTo(b.x + len * 0.3, y - w * 0.4, b.x + len * 0.5, y, b.x + len * 0.7, y)
          .bezierCurveTo(b.x + len * 0.5, y + w * 0.1, b.x + len * 0.3, y + w * 0.4, b.x - 2, y + w * 0.35)
          .fill({ color: lighten(mix(col, 0xffe066, 0.6), 0.3), alpha: 0.9 });
      }
      break;
    }
    default:
      break;
  }
}

// ─── Autocollants ─────────────────────────────────────────────────────

/** Dessine un autocollant centré en (x, y), de taille s (dans le repère de la caisse, nez vers +x). */
export function drawDecal(g: Graphics, id: string, x: number, y: number, s: number, color: number): void {
  const edge = { width: Math.max(0.6, s * 0.06), color: darken(color, 0.7), alpha: 0.9 };
  const P = (pts: [number, number][]) => g.poly(pts.flatMap(([px, py]) => [x + px * s, y + py * s]));
  switch (id) {
    case 'dec:etoile': {
      const pts: [number, number][] = [];
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const r = i % 2 ? 0.2 : 0.5;
        pts.push([Math.cos(a) * r, Math.sin(a) * r]);
      }
      P(pts).fill(color).stroke(edge);
      break;
    }
    case 'dec:chevrons':
      for (const dx of [-0.25, 0.1]) P([[dx - 0.15, -0.4], [dx + 0.2, 0], [dx - 0.15, 0.4], [dx - 0.32, 0.4], [dx + 0.03, 0], [dx - 0.32, -0.4]]).fill(color).stroke(edge);
      break;
    case 'dec:cible':
      g.circle(x, y, s * 0.48).stroke({ width: s * 0.1, color });
      g.circle(x, y, s * 0.26).stroke({ width: s * 0.09, color });
      g.circle(x, y, s * 0.08).fill(color);
      break;
    case 'dec:eclair':
      P([[0.05, -0.5], [-0.25, 0.05], [-0.02, 0.05], [-0.12, 0.5], [0.25, -0.08], [0.02, -0.08], [0.15, -0.5]]).fill(color).stroke(edge);
      break;
    case 'dec:flamme':
      g.moveTo(x - 0.45 * s, y)
        .bezierCurveTo(x - 0.2 * s, y - 0.5 * s, x + 0.2 * s, y - 0.25 * s, x + 0.5 * s, y - 0.05 * s)
        .bezierCurveTo(x + 0.25 * s, y + 0.05 * s, x + 0.05 * s, y + 0.45 * s, x - 0.45 * s, y)
        .fill(color)
        .stroke(edge);
      break;
    case 'dec:griffe':
      for (const dy of [-0.28, 0, 0.28]) P([[-0.45, dy - 0.06], [0.4, dy - 0.12], [0.48, dy], [-0.4, dy + 0.06]]).fill(color);
      break;
    case 'dec:ailes':
      for (const side of [-1, 1]) {
        P([[0, 0], [-0.1, side * 0.15], [-0.45, side * 0.5], [-0.25, side * 0.48], [-0.32, side * 0.35], [-0.12, side * 0.36], [-0.18, side * 0.22], [0.05, side * 0.08]]).fill(color).stroke(edge);
      }
      g.circle(x + 0.08 * s, y, 0.09 * s).fill(color);
      break;
    case 'dec:couronne':
      P([[-0.3, -0.42], [0.3, -0.42], [0.3, 0.42], [-0.3, 0.42], [0.12, 0.26], [-0.08, 0.12], [0.18, 0], [-0.08, -0.12], [0.12, -0.26]]).fill(color).stroke(edge);
      break;
    case 'dec:crane':
      // Crâne stylisé (vu de dessus, nez vers +x) : boîte crânienne, orbites, dents.
      g.roundRect(x - 0.42 * s, y - 0.36 * s, 0.62 * s, 0.72 * s, 0.3 * s).fill(color).stroke(edge);
      g.roundRect(x + 0.12 * s, y - 0.2 * s, 0.24 * s, 0.4 * s, 0.06 * s).fill(color).stroke(edge);
      g.circle(x - 0.02 * s, y - 0.15 * s, 0.11 * s).fill(darken(color, 0.8));
      g.circle(x - 0.02 * s, y + 0.15 * s, 0.11 * s).fill(darken(color, 0.8));
      for (const dy of [-0.1, 0, 0.1]) g.rect(x + 0.24 * s, y + dy * s - 0.03 * s, 0.1 * s, 0.05 * s).fill(darken(color, 0.7));
      break;
    case 'dec:dragon': {
      // Dragon enroulé : corps en S, ailes, tête vers +x.
      g.moveTo(x - 0.5 * s, y + 0.3 * s)
        .bezierCurveTo(x - 0.1 * s, y + 0.55 * s, x - 0.2 * s, y - 0.1 * s, x + 0.1 * s, y - 0.05 * s)
        .bezierCurveTo(x + 0.3 * s, y, x + 0.35 * s, y - 0.25 * s, x + 0.5 * s, y - 0.22 * s)
        .stroke({ width: 0.16 * s, color, cap: 'round' });
      P([[0.05, -0.05], [-0.15, -0.5], [0.0, -0.35], [0.1, -0.48], [0.15, -0.2]]).fill(color).stroke(edge);
      P([[0.44, -0.3], [0.58, -0.22], [0.44, -0.14]]).fill(color);
      g.circle(x + 0.45 * s, y - 0.24 * s, 0.03 * s).fill(darken(color, 0.8));
      break;
    }
    default:
      break;
  }
}

// ─── Assemblage ───────────────────────────────────────────────────────

/** Couche d'une pièce : base + (matière, motif, autocollant) découpés par la silhouette. */
function paintPart(parent: Container, p: PartPaint, shape: (g: Graphics) => Box, seed: number, decal?: { x: number; y: number; s: number }[]): Box {
  const base = new Graphics();
  const box = shape(base);
  base.fill(baseFill(p)).stroke({ width: 2.5, color: outlineOf(p.color) });
  parent.addChild(base);
  const detail = new Graphics();
  materialOverlay(detail, p, box, seed);
  patternOverlay(detail, p, box, seed);
  if (p.dec && decal) for (const d of decal) drawDecal(detail, p.dec, d.x, d.y, d.s, p.decColor);
  if (p.mat === 'mat:neon') {
    // Néon : liseré lumineux à l'intérieur de la silhouette.
    shape(detail);
    detail.stroke({ width: 5, color: lighten(p.color, 0.2), alpha: 0.35 });
    shape(detail);
    detail.stroke({ width: 1.6, color: lighten(p.color, 0.65), alpha: 1 });
  }
  const mask = new Graphics();
  shape(mask);
  mask.fill(0xffffff);
  detail.mask = mask;
  parent.addChild(detail, mask);
  // Contour par-dessus le détail : la silhouette reste nette quel que soit le motif.
  const edge = new Graphics();
  shape(edge);
  edge.stroke({ width: 2.5, color: outlineOf(p.color) });
  parent.addChild(edge);
  return box;
}

/** Échantillon d'une peinture (matière, motif, autocollant) sur une plaque arrondie : vignettes de l'atelier. */
export function drawSwatch(p: PartPaint, w: number, h: number): Container {
  const c = new Container();
  paintPart(c, p, (g) => {
    g.roundRect(-w / 2, -h / 2, w, h, Math.min(w, h) * 0.16);
    return { x: -w / 2, y: -h / 2, w, h };
  }, 7, p.dec ? [{ x: 0, y: 0, s: Math.min(w, h) * 0.7 }] : undefined);
  return c;
}

/** Palette « classique » dérivée d'une pièce, pour les détails de famille et les tubes. */
function paletteOf(p: PartPaint, outlineFrom = p.color): Palette {
  return { hull: p.color, plate: lighten(p.color, 0.18), tread: darken(p.color, 0.3), barrel: p.color, band: darken(p.color, 0.32), outline: outlineOf(outlineFrom) };
}

export interface PaintedTank {
  hull: Container;
  turret: Container;
  auto: Graphics;
  hasAuto: boolean;
}

/**
 * Tank complet peint selon `look` : caisse (chenilles + caisse + détails de famille et galons), tourelle (tubes + dôme,
 * cœur à la couleur du camp), tourelles automatiques. Même repère et même échelle que drawTankParts.
 */
export function drawPaintedTank(cls: ClassNode, look: TankLook, accent: number, seedBase = 1, decor = 0): PaintedTank {
  const L = look.parts;
  const hull = new Container();
  if (decor === 2) {
    // Châssis lourd : plaques latérales sous les chenilles, comme le dessin classique (hitbox inchangée).
    const tp = paletteOf(L.treads);
    const plates = new Graphics();
    for (const side of [-1, 1]) plates.roundRect(-0.6 * R, side * 0.86 * R - 0.08 * R, 1.2 * R, 0.16 * R, 0.05 * R).fill(tp.band).stroke({ width: 2, color: tp.outline });
    hull.addChild(plates);
  }
  paintPart(hull, L.treads, treadsShape, seedBase * 11 + 1, [
    { x: 0, y: -0.66 * R, s: 0.36 * R },
    { x: 0, y: 0.66 * R, s: 0.36 * R },
  ]);
  paintPart(hull, L.hull, hullShape, seedBase * 11 + 2);
  shadeHull(hull, seedBase);
  const hp = paletteOf(L.hull);
  const det = new Graphics();
  // Nez (sens de la caisse) et rivets, puis détails propres à la famille et galons de rang.
  det.poly([0.64 * R, -0.18 * R, 0.76 * R, 0, 0.64 * R, 0.18 * R]).fill(hp.plate).stroke({ width: 1.5, color: hp.outline });
  if (decor !== 1) for (const [x, y] of [[-0.52, -0.4], [0.52, -0.4], [-0.52, 0.4], [0.52, 0.4]]) det.circle(x * R, y * R, 0.05 * R).fill(hp.outline);
  drawHullFamily(det, cls, hp);
  drawFamilyDetail(det, cls, hp);
  drawRankPips(det, cls, hp);
  hull.addChild(det);

  const turret = new Container();
  const bp = paletteOf(L.barrels);
  const barrels = cls.weapon.barrels.filter((b) => !b.auto);
  const barrelShape = (g: Graphics): Box => {
    // Silhouette des tubes : on retrace chaque tube en blanc (même géométrie que drawBarrel).
    const white: Palette = { hull: 0xffffff, plate: 0xffffff, tread: 0xffffff, barrel: 0xffffff, band: 0xffffff, outline: 0xffffff };
    for (const b of barrels) drawBarrel(g, b, white, 0xffffff);
    return { x: -1.6 * R, y: -1.6 * R, w: 3.2 * R, h: 3.2 * R };
  };
  if (barrels.length) {
    // Les tubes gardent leur dessin (embouchures, trappes à la couleur du camp) ; matière et motif s'y ajoutent.
    const base = new Graphics();
    for (const b of barrels) drawBarrel(base, b, bp, accent);
    turret.addChild(base);
    const detail = new Graphics();
    const box = { x: -0.2 * R, y: -1.6 * R, w: 2.4 * R, h: 3.2 * R };
    materialOverlay(detail, L.barrels, box, seedBase * 11 + 3);
    patternOverlay(detail, L.barrels, box, seedBase * 11 + 3);
    const mask = new Graphics();
    barrelShape(mask);
    detail.mask = mask;
    // Contour des tubes, puis embouchures et pièces à la couleur du camp par-dessus le motif.
    // Remplissage transparent (style Pixi accepté par fill) : seuls les traits de drawBarrel restent.
    const clear = { color: 0, alpha: 0 } as unknown as number;
    const edge = new Graphics();
    for (const b of barrels) drawBarrel(edge, b, { hull: clear, plate: clear, tread: clear, barrel: clear, band: clear, outline: bp.outline }, clear);
    const tips = new Graphics();
    for (const b of barrels) drawBarrel(tips, b, bp, accent, true);
    // Ombrage des tubes sous les contours et sous les pièces à la couleur du camp (jamais assombries).
    turret.addChild(detail, mask);
    shadeBarrels(turret, cls);
    turret.addChild(edge, tips);
  }
  paintPart(turret, L.turret, domeShape, seedBase * 11 + 4);
  shadeDome(turret);
  glossCore(turret, accent, outlineOf(L.turret.color));

  const auto = new Graphics();
  const autos = cls.weapon.barrels.filter((b) => b.auto);
  if (autos.length) {
    for (const b of autos) drawBarrel(auto, b, bp, accent);
    for (const b of autos) {
      const a = (b.angle * Math.PI) / 180;
      const ox = -Math.sin(a) * b.offset * R;
      const oy = Math.cos(a) * b.offset * R;
      auto.circle(ox, oy, 0.24 * R).fill(paletteOf(L.turret).plate).stroke({ width: 1.5, color: outlineOf(L.turret.color) });
      auto.circle(ox, oy, 0.1 * R).fill(accent);
    }
  }
  return { hull, turret, auto, hasAuto: autos.length > 0 };
}

/** Pièce → libellé court (éditeur, planche). */
export const PART_LABEL: Record<PartId, string> = { hull: 'Caisse', treads: 'Chenilles', turret: 'Tourelle', barrels: 'Canons' };
